<template>
  <div class="warm-entry-flow">
    <TransitionGroup name="fade-slide">
      <div v-if="showWelcome" key="welcome" class="welcome-screen">
        <Card class="text-center max-w-md mx-auto">
          <div class="space-y-4 p-6">
            <div class="text-5xl" aria-hidden="true">7</div>
            <h1 class="text-2xl font-bold text-rogue-deep-forest">
              {{ t('sevenFacts.welcome.firstTime') }}
            </h1>
            <p class="text-gray-600">
              {{ t('sevenFacts.welcome.tagline') }}
            </p>
            <Button variant="primary" class="animated-pulse" @click="startEntry">
              {{ t('sevenFacts.welcome.startButton') }}
            </Button>
          </div>
        </Card>
      </div>

      <div v-else key="entry" class="entry-screen">
        <Card class="max-w-2xl mx-auto">
          <div class="mb-6">
            <ProgressRing
              :progress="progressPercentage"
              :show-milestone="showMilestone"
              :label="t('sevenFacts.onboarding.progress.label')"
              @milestone-reached="celebrateMilestone"
            />
            <p class="text-center text-sm text-gray-600 mt-2">
              {{ progressMessage }}
            </p>
            <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div
                v-for="milestone in progressMilestones"
                :key="milestone.count"
                class="rounded-md border px-3 py-2"
                :class="
                  milestone.active
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-neutral text-gray-600'
                "
              >
                {{ milestone.label }}
              </div>
            </div>
          </div>

          <div class="text-center mb-6">
            <h2 class="text-2xl font-bold text-rogue-deep-forest mb-2">
              {{ t('sevenFacts.onboarding.starter.title') }}
            </h2>
            <p class="text-gray-600">
              {{ t('sevenFacts.onboarding.starter.subtitle') }}
            </p>
          </div>

          <div class="space-y-4">
            <TransitionGroup name="fact-reveal">
              <div
                v-for="(fact, index) in visibleFacts"
                :key="index"
                :data-index="index"
                class="fact-input-wrapper"
              >
                <div class="relative">
                  <Input
                    v-model="facts[index]"
                    :placeholder="getPlaceholder(index)"
                    maxlength="160"
                    class="pr-16"
                    :disabled="isSaving"
                    :aria-invalid="Boolean(getFactError(index))"
                    @update:model-value="value => handleFactUpdate(index, value)"
                  />
                  <span
                    class="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-400"
                  >
                    {{ facts[index].length }}/160
                  </span>
                </div>

                <p v-if="getFactError(index)" class="mt-2 text-sm text-red-600">
                  {{ getFactError(index) }}
                </p>

                <Transition name="encouragement">
                  <div
                    v-if="showEncouragement[index]"
                    class="mt-2 text-sm text-rogue-vibrant-green font-medium"
                  >
                    {{ getEncouragement(index) }}
                  </div>
                </Transition>
              </div>
            </TransitionGroup>
          </div>

          <div
            v-if="currentError"
            class="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {{ currentError }}
          </div>

          <div class="mt-8 space-y-3">
            <Button
              variant="primary"
              class="w-full"
              :disabled="!canSubmit || isSaving"
              @click="submitFacts"
            >
              <Loader2 v-if="isSaving" class="h-4 w-4 mr-2 animate-spin" />
              {{ isSaving ? t('sevenFacts.saving') : t('sevenFacts.gentle.playNow') }}
            </Button>

            <div class="flex flex-col sm:flex-row gap-3">
              <Button
                v-if="canAddMore"
                variant="outline"
                class="flex-1"
                :disabled="isSaving"
                @click="revealNextInput"
              >
                {{ t('sevenFacts.gentle.addMore') }}
              </Button>

              <Button
                variant="ghost"
                class="flex-1"
                :disabled="isSaving"
                @click="showInspiration = !showInspiration"
              >
                {{ t('sevenFacts.gentle.inspiration') }}
              </Button>
            </div>
          </div>

          <Transition name="inspiration">
            <div
              v-if="showInspiration"
              class="mt-4 rounded-lg border border-neutral bg-gray-50 p-4"
            >
              <h4 class="font-medium text-gray-700 mb-2">
                {{ t('sevenFacts.gentle.examples') }}
              </h4>
              <div class="grid grid-cols-1 gap-2 text-sm text-gray-600">
                <button
                  v-for="example in inspirationExamples"
                  :key="example"
                  type="button"
                  class="text-left p-3 bg-white rounded-md border border-neutral hover:border-primary hover:text-primary transition-colors"
                  :disabled="isSaving"
                  @click="insertExample(example)"
                >
                  {{ example }}
                </button>
              </div>
            </div>
          </Transition>
        </Card>
      </div>
    </TransitionGroup>

    <div class="fixed top-4 right-4 z-50">
      <Achievement
        v-for="achievement in activeAchievements"
        :key="achievement.id"
        :show="true"
        :title="achievement.title"
        :description="achievement.description"
        :icon="achievement.icon"
        class="mb-2"
        @close="dismissAchievement(achievement.id)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Loader2 } from 'lucide-vue-next'
import { supabase } from '@/lib/supabase'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import ProgressRing from '@/components/ui/ProgressRing.vue'
import Achievement from '@/components/ui/Achievement.vue'

const { t } = useI18n()

const props = defineProps({
  isFirstTime: {
    type: Boolean,
    default: true
  },
  existingFacts: {
    type: Array,
    default: () => []
  },
  onSubmit: {
    type: Function,
    default: null
  },
  isSubmitting: {
    type: Boolean,
    default: false
  }
})

let revealTimer = null

const showWelcome = ref(props.isFirstTime)
const facts = ref(Array(7).fill(''))
const visibleInputs = ref(3)
const showEncouragement = ref(Array(7).fill(false))
const showMilestone = ref(false)
const showInspiration = ref(false)
const localSubmitting = ref(false)
const submitError = ref('')
const activeAchievements = ref([])
const inspirationExamples = ref([])

const suggestionFacts = [
  'I like slow conversations before anything gets playful.',
  'I enjoy clear invitations, especially when no is easy to say.',
  'I flirt best when there is no pressure to act on it.',
  'I like touch, but I prefer to negotiate it before it happens.',
  'I enjoy power exchange more when there is room for laughter.',
  'I can be sensual without wanting things to become sexual.',
  'I like intense play, but I need a soft landing afterwards.',
  'I am curious about switching with people who help me feel safe.',
  'I may seem quiet, but I often notice a lot.',
  'I like being desired, but I do not like being chased.',
  'I enjoy giving care, and I am learning to receive it too.',
  'I am open to surprising connections if we move slowly and honestly.'
]

const visibleFacts = computed(() => facts.value.slice(0, visibleInputs.value))
const normalizedFacts = computed(() => facts.value.map(fact => fact.trim()).filter(Boolean))
const validFacts = computed(() =>
  normalizedFacts.value.filter(fact => fact.length >= 10 && fact.length <= 160)
)
const factCount = computed(() => validFacts.value.length)
const progressPercentage = computed(() => (factCount.value / 7) * 100)
const duplicateKeys = computed(() => {
  const counts = new Map()
  normalizedFacts.value.forEach(fact => {
    const key = fact.toLocaleLowerCase()
    counts.set(key, (counts.get(key) || 0) + 1)
  })
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key))
})
const hasDuplicateFacts = computed(() => duplicateKeys.value.size > 0)
const hasInvalidVisibleFacts = computed(() =>
  visibleFacts.value.some(fact => {
    const text = fact.trim()
    return text.length > 0 && text.length < 10
  })
)
const canSubmit = computed(
  () =>
    validFacts.value.length >= 3 &&
    validFacts.value.length <= 7 &&
    !hasDuplicateFacts.value &&
    !hasInvalidVisibleFacts.value
)
const canAddMore = computed(() => visibleInputs.value < 7)
const isSaving = computed(() => props.isSubmitting || localSubmitting.value)
const progressMessage = computed(() => {
  if (factCount.value >= 7) return t('sevenFacts.onboarding.progress.complete')
  if (factCount.value >= 5)
    return t('sevenFacts.onboarding.progress.strong', { count: factCount.value })
  if (factCount.value >= 3)
    return t('sevenFacts.onboarding.progress.unlocked', { count: factCount.value })
  return t('sevenFacts.onboarding.progress.minimal', { count: factCount.value })
})
const progressMilestones = computed(() => [
  {
    count: 3,
    label: t('sevenFacts.onboarding.progress.three'),
    active: factCount.value >= 3
  },
  {
    count: 5,
    label: t('sevenFacts.onboarding.progress.five'),
    active: factCount.value >= 5
  },
  {
    count: 7,
    label: t('sevenFacts.onboarding.progress.seven'),
    active: factCount.value >= 7
  }
])
const validationError = computed(() => {
  if (hasDuplicateFacts.value) return t('sevenFacts.ensureUnique')
  if (hasInvalidVisibleFacts.value) return t('sevenFacts.factLengthHint')
  if (validFacts.value.length > 0 && validFacts.value.length < 3) {
    return t('sevenFacts.enterAtLeastThree')
  }
  return ''
})
const currentError = computed(() => submitError.value || validationError.value)

watch(
  () => props.existingFacts,
  existingFacts => {
    loadExistingFacts(existingFacts)
  },
  { immediate: true }
)

watch(showInspiration, async newValue => {
  if (newValue && inspirationExamples.value.length === 0) {
    await loadExampleFacts()
  }
})

onUnmounted(() => clearTimeout(revealTimer))

function loadExistingFacts(existingFacts) {
  const existing = existingFacts
    .map(fact => String(fact || '').trim())
    .filter(Boolean)
    .slice(0, 7)
  facts.value = [...existing, ...Array(7 - existing.length).fill('')]
  visibleInputs.value = Math.max(3, existing.length)
  showWelcome.value = props.isFirstTime && existing.length === 0
}

function pickExampleFacts(exampleFacts) {
  return [...exampleFacts].sort(() => Math.random() - 0.5).slice(0, 5)
}

async function loadExampleFacts() {
  try {
    const { data, error } = await supabase
      .from('fact_suggestions')
      .select('text')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })

    if (error) throw error

    const databaseSuggestions = data?.map(suggestion => suggestion.text).filter(Boolean) || []
    inspirationExamples.value = pickExampleFacts(
      databaseSuggestions.length > 0 ? databaseSuggestions : suggestionFacts
    )
  } catch (error) {
    console.error('Error fetching fact suggestions:', error)
    inspirationExamples.value = pickExampleFacts(suggestionFacts)
  }
}

function startEntry() {
  showWelcome.value = false
}

function handleFactUpdate(index, value) {
  submitError.value = ''
  facts.value[index] = value

  if (value.trim().length >= 10 && !showEncouragement.value[index]) {
    showEncouragement.value[index] = true
    setTimeout(() => {
      showEncouragement.value[index] = false
    }, 3000)
  }

  if (value.trim().length >= 10 && index === visibleInputs.value - 1 && visibleInputs.value < 7) {
    revealTimer = setTimeout(() => revealNextInput(), 500)
  }
}

function revealNextInput() {
  if (visibleInputs.value < 7) {
    visibleInputs.value++
    nextTick(() => {
      const newInput = document.querySelector(
        `.fact-input-wrapper[data-index="${visibleInputs.value - 1}"]`
      )
      newInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }
}

async function insertExample(example) {
  submitError.value = ''
  let targetIndex = facts.value.findIndex(
    (fact, index) => index < visibleInputs.value && !fact.trim()
  )

  if (targetIndex === -1 && visibleInputs.value < 7) {
    revealNextInput()
    await nextTick()
    targetIndex = visibleInputs.value - 1
  }

  if (targetIndex === -1) {
    targetIndex = facts.value.findIndex(fact => fact.trim().length < 10)
  }

  if (targetIndex === -1) return

  facts.value[targetIndex] = example.slice(0, 160)
}

function getPlaceholder(index) {
  return t(`sevenFacts.onboarding.starter.placeholder${index + 1}`) || t('sevenFacts.factPlaceholder')
}

function getEncouragement(index) {
  return t(`sevenFacts.onboarding.encouragement.${index % 4}`)
}

function getFactError(index) {
  const text = facts.value[index].trim()
  if (!text) return ''
  if (text.length < 10) return t('sevenFacts.factLengthHint')
  if (duplicateKeys.value.has(text.toLocaleLowerCase())) return t('sevenFacts.ensureUnique')
  return ''
}

function celebrateMilestone(factCount) {
  showMilestone.value = true
  setTimeout(() => {
    showMilestone.value = false
  }, 2000)

  let achievement
  if (factCount === 3) {
    achievement = {
      id: Date.now(),
      title: t('sevenFacts.achievements.firstThree.title'),
      description: t('sevenFacts.achievements.firstThree.desc'),
      icon: '3'
    }
  } else if (factCount === 5) {
    achievement = {
      id: Date.now(),
      title: t('sevenFacts.achievements.fiveFacts.title'),
      description: t('sevenFacts.achievements.fiveFacts.desc'),
      icon: '5'
    }
  } else if (factCount === 7) {
    achievement = {
      id: Date.now(),
      title: t('sevenFacts.achievements.fullSeven.title'),
      description: t('sevenFacts.achievements.fullSeven.desc'),
      icon: '7'
    }
  }

  if (achievement) {
    activeAchievements.value.push(achievement)
    setTimeout(() => dismissAchievement(achievement.id), 5000)
  }
}

function dismissAchievement(id) {
  const index = activeAchievements.value.findIndex(achievement => achievement.id === id)
  if (index > -1) {
    activeAchievements.value.splice(index, 1)
  }
}

async function submitFacts() {
  submitError.value = ''
  if (!canSubmit.value) {
    submitError.value = validationError.value || t('sevenFacts.enterAtLeastThree')
    return
  }

  localSubmitting.value = true
  try {
    const factsToSubmit = normalizedFacts.value.slice(0, 7)
    if (props.onSubmit) {
      await props.onSubmit(factsToSubmit)
    }
  } catch (error) {
    console.error('Error saving facts:', error)
    submitError.value = error?.message || t('sevenFacts.submitFailed')
  } finally {
    localSubmitting.value = false
  }
}
</script>

<style scoped>
.animated-pulse {
  animation: gentle-pulse 2s infinite;
}

@keyframes gentle-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.5s ease-out;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.fact-reveal-enter-active {
  transition: all 0.3s ease-out;
}

.fact-reveal-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.encouragement-enter-active,
.encouragement-leave-active {
  transition: all 0.3s ease;
}

.encouragement-enter-from,
.encouragement-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.inspiration-enter-active,
.inspiration-leave-active {
  transition: all 0.3s ease;
}

.inspiration-enter-from,
.inspiration-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
